
import { useRef, useState } from "react";
import { Canvas, Image as FabricImage } from 'fabric';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface FileUploadInputProps {
  canvas: Canvas | null;
  onUploadStart?: () => void;
  onUploadEnd?: () => void;
  onImageAdded: () => void;
  onToolChange: (tool: string) => void;
}

export const FileUploadInput = ({ 
  canvas, 
  onUploadStart, 
  onUploadEnd, 
  onImageAdded,
  onToolChange 
}: FileUploadInputProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!canvas || !event.target.files || event.target.files.length === 0) return;

    setIsUploading(true);
    onUploadStart?.();

    const file = event.target.files[0];
    const fileExt = file.name.split('.').pop();
    const fileName = `${Math.random()}.${fileExt}`;
    const filePath = `${fileName}`;

    try {
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('whiteboard-images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('whiteboard-images')
        .getPublicUrl(filePath);

      FabricImage.fromURL(publicUrl, {
        crossOrigin: 'anonymous'
      }).then((img) => {
        const maxDim = 500; // Changed from 600 to 500
        if (img.width && img.height) {
          if (img.width > maxDim || img.height > maxDim) {
            const scale = maxDim / Math.max(img.width, img.height);
            img.scale(scale);
          }
        }

        img.set({
          left: (canvas.width || 0) / 2 - ((img.width || 0) * (img.scaleX || 1)) / 2,
          top: (canvas.height || 0) / 2 - ((img.height || 0) * (img.scaleY || 1)) / 2,
          rx: 8, // radius for rounded corners
          ry: 8  // radius for rounded corners
        });

        canvas.add(img);
        canvas.setActiveObject(img);
        canvas.renderAll();
        onToolChange("transform"); // Switch to transform tool
        onImageAdded();

        toast({
          title: "Image uploaded",
          description: "The image has been added to your whiteboard.",
        });
      });
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        variant: "destructive",
        title: "Upload failed",
        description: "There was an error uploading your image. Please try again.",
      });
    } finally {
      setIsUploading(false);
      onUploadEnd?.();
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <input
      type="file"
      ref={fileInputRef}
      id="imageUpload"
      accept="image/*"
      onChange={handleImageUpload}
      className="hidden"
    />
  );
};
